import { DndContext, PointerSensor, TouchSensor, rectIntersection, useSensor, useSensors } from "@dnd-kit/core";
import { restrictToVerticalAxis, restrictToWindowEdges, snapCenterToCursor } from "@dnd-kit/modifiers";
import { SortableContext, arrayMove } from "@dnd-kit/sortable";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";

import { useCan } from "components";
import { SortableItem, SortableOverlay } from "modules/PendingChecklistsDashboard/components/PinChecklists/components/PinChecklistModal/components";
import { useReorderPinPendingChecklistMutation } from "modules/PendingChecklistsDashboard/components/PinChecklists/components/PinChecklistModal/components/SortableList/hooks";
import "modules/PendingChecklistsDashboard/components/PinChecklists/components/PinChecklistModal/components/SortableList/SortableList.scss";
import { checkIfAannameChecklistsAreAtTheTop } from "modules/PendingChecklistsDashboard/util";

import type { DragEndEvent } from "@dnd-kit/core";
import type { Checklist } from "models";
import type { SortablePinPendingChecklists } from "modules/PendingChecklistsDashboard/components/PinChecklists/components/PinChecklistModal";
import type { Dispatch, SetStateAction } from "react";

type SortableListProps = {
  sortablePinPendingChecklists: SortablePinPendingChecklists[];
  setSortablePinPendingChecklists: Dispatch<SetStateAction<SortablePinPendingChecklists[]>>;
  checklists?: Checklist[];
};

export const SortableList = ({ sortablePinPendingChecklists, setSortablePinPendingChecklists, checklists }: SortableListProps) => {
  const { t } = useTranslation();

  const sensors = useSensors(useSensor(PointerSensor), useSensor(TouchSensor, { activationConstraint: { delay: 250, tolerance: 100 } }));

  const canReorderPinPendingChecklist = useCan("reorder", "pin-pending-checklist");
  const { mutateAsync: reorderPinPendingChecklistMutation } = useReorderPinPendingChecklistMutation();

  const handleOnDragEnd = async ({ active, over }: DragEndEvent) => {
    if (!over || active.id === over.id) return;

    try {
      const newIndex = sortablePinPendingChecklists.findIndex(item => item.id === over.id);
      const oldIndex = sortablePinPendingChecklists.findIndex(item => item.id === active.id);
      const sortedPinPendingChecklists = arrayMove(sortablePinPendingChecklists, oldIndex, newIndex).map((i, idx) => ({ ...i, ordering: idx + 1 }));

      if (checklists && !checkIfAannameChecklistsAreAtTheTop(sortedPinPendingChecklists, checklists)) {
        toast.error(t("v8_invalid_order_aanname_checklists_must_be_at_the_top_of_the_list", "Invalid order: Aanname checklists must be at the top of the list"));
        return;
      }

      setSortablePinPendingChecklists(sortedPinPendingChecklists);
      await reorderPinPendingChecklistMutation(sortedPinPendingChecklists);
    } catch (error) {
      setSortablePinPendingChecklists(sortablePinPendingChecklists);
      console.error(error);
    }
  };

  return (
    <div className="PinPendingChecklistsSortableList">
      <DndContext
        sensors={sensors}
        onDragEnd={handleOnDragEnd}
        collisionDetection={rectIntersection}
        modifiers={[restrictToVerticalAxis, restrictToWindowEdges, snapCenterToCursor]}
      >
        <>
          <SortableContext items={sortablePinPendingChecklists} disabled={!canReorderPinPendingChecklist}>
            <ul className="check-container">
              {sortablePinPendingChecklists.map(item => (
                <SortableItem key={`pending-checklist-${item.checklist_id}`} item={item} checklists={checklists} />
              ))}
            </ul>
          </SortableContext>
          <SortableOverlay />
        </>
      </DndContext>
    </div>
  );
};
